import React, { useState, useMemo, useCallback, useEffect } from "react";

import {
  Info,
  ArrowDown,
  ArrowUp,
  AlertTriangle,
  Building2,
  CheckCircle2,
  MapPin,
  Users,
  Zap,
} from "lucide-react";

import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import SearchInput from "../../Components/Search/SearchInput";
import Select from "../../Components/Select";
import TextArea from "../../Components/TextArea";
import { ExecutiveFromAPI } from "../../Store/Reducers/AssignmentSlice";
import type { AdminUser } from "../../Store/Reducers/MasterSlice";
import {
  APPROVER_SELECT_ROLE_GROUPS,
  normalizeRoleName,
  type RoleType,
} from "../../Utils/Constants/RoleConstants";
import { useAccountTiers } from "../../Utils/Helper/accountTierHelpers";
import { formatDateUSShort } from "../../Utils/Helper/Helper";

// Types
export type ExecutiveForUI = {
  id: string;
  name: string;
  title: string;
  availability: string;
  utilization: number;
  region: string;
  match: number;
  warnings: string[];
  recommendations: string[];
  /** Total capacity from API (accountLimit) */
  accountLimit: number;
  /** Current usage from API (current_accounts) */
  currentAccounts: number;
};

export type UnassignedAccount = {
  id: string;
  name: string;
  industry: string;
  location: string;
  tier: string;
  value: string;
  status: string;
};

// Constants
const MATCH_THRESHOLDS = {
  HIGH: 80,
  MEDIUM: 60,
} as const;

const getMatchLevel = (match: number): "high" | "medium" | "low" => {
  if (match >= MATCH_THRESHOLDS.HIGH) return "high";
  if (match >= MATCH_THRESHOLDS.MEDIUM) return "medium";
  return "low";
};

// AI Recommendations Banner - reusable with dynamic tier
export type AIRecommendationsBannerProps = {
  tier?: string | null;
  variant?: "single" | "bulk";
};

export const AIRecommendationsBanner: React.FC<
  AIRecommendationsBannerProps
> = ({ tier, variant = "single" }) => {
  const { formatTier } = useAccountTiers();
  const tierLabel = formatTier(tier) || tier;
  const tierText = tierLabel ? `${tierLabel} tier` : "account tier";
  const description =
    variant === "bulk"
      ? `Based on utilization (${tierText}), region matching, and executive expertise. Use the tabs below to review and adjust executive assignments for each account.`
      : `Based on utilization (${tierText}), region matching, and executive expertise. Top recommendations are highlighted below.`;

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 p-3 rounded-xl mb-4">
      <div className="flex items-start gap-3">
        <Zap size={20} className="text-blue-600 shrink-0 mt-0.5" aria-hidden />
        <div>
          <h6 className="text-sm font-semibold text-purple-900 uppercase tracking-wide mb-2 m-0">
            AI Recommendations
          </h6>
          <p className="mb-0 text-gray-700 text-sm leading-relaxed font-normal">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

/** Default batch name: "Assignment - {accountName} - {today's date}" using utils date format */
export const getDefaultBatchName = (accountName: string): string => {
  const today = new Date().toISOString();
  const dateStr = formatDateUSShort(today, true);
  return `Assignment - ${accountName || "Account"} - ${dateStr}`;
};

/** Default batch name for edit flow: "Edit Assignment - {accountName} - {today's date}" */
export const getDefaultEditBatchName = (accountName: string): string => {
  const today = new Date().toISOString();
  const dateStr = formatDateUSShort(today, true);
  return `Edit Assignment - ${accountName || "Account"} - ${dateStr}`;
};

// Capacity utilization bar with segmented progress and legend
export type CapacityUtilizationBarProps = {
  /** Active (utilzsed) accounts */
  active: number;
  /** Pending activation accounts */
  pending?: number;
  /** Total capacity (accountLimit) */
  total: number;
  /** Optional class name */
  className?: string;
  /** Compact variant for cards (smaller text) */
  compact?: boolean;
};

export const CapacityUtilizationBar: React.FC<CapacityUtilizationBarProps> = ({
  active,
  pending = 0,
  total,
  className = "",
  compact = true,
}) => {
  const safeTotal = Math.max(0, total);
  const totalUtilized = active + pending;
  const free = Math.max(0, safeTotal - totalUtilized);
  const utilizationPercent =
    safeTotal > 0 ? Math.round((totalUtilized / safeTotal) * 100) : 0;
  const isOverCapacity = totalUtilized > safeTotal && safeTotal > 0;

  const totalForBar = safeTotal || 1;
  const totalForSegments = totalUtilized || 1;
  const activePercent =
    totalUtilized > safeTotal
      ? (active / totalForSegments) * 100
      : (active / totalForBar) * 100;
  const pendingPercent =
    totalUtilized > safeTotal
      ? (pending / totalForSegments) * 100
      : (pending / totalForBar) * 100;
  const freePercent =
    totalUtilized > safeTotal
      ? 0
      : Math.max(0, 100 - activePercent - pendingPercent);

  return (
    <div className={`flex flex-col items-end gap-1.5 min-w-0 ${className}`}>
      <div className="flex items-center gap-3 w-full max-w-[220px]">
        <div className="flex-1 min-w-0 h-2 rounded-full overflow-hidden flex bg-gray-200">
          {active > 0 && (
            <div
              className="h-full bg-green-500 flex-shrink-0"
              style={{ width: `${activePercent}%` }}
            />
          )}
          {pending > 0 && (
            <div
              className="h-full bg-orange-500 flex-shrink-0"
              style={{ width: `${pendingPercent}%` }}
            />
          )}
          {free > 0 && (
            <div
              className="h-full bg-gray-300 flex-shrink-0"
              style={{ width: `${freePercent}%` }}
            />
          )}
        </div>
        <span
          className={`font-medium whitespace-nowrap shrink-0 ${
            isOverCapacity ? "text-amber-600" : "text-gray-700"
          } ${compact ? "text-xs" : "text-sm"}`}
        >
          {totalUtilized}/{safeTotal} Utilized ({utilizationPercent}%)
        </span>
      </div>
      <div
        className={`flex flex-wrap items-center gap-x-4 gap-y-0.5 text-gray-600 ${
          compact ? "text-[11px]" : "text-xs"
        }`}
      >
        <span className="flex items-center gap-1.5 font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
          <span>{active} active</span>
        </span>
        <span className="flex items-center gap-1.5 font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
          <span>{pending} pending</span>
        </span>
        <span className="flex items-center gap-1.5 font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
          <span>{free} free</span>
        </span>
      </div>
    </div>
  );
};

const getBorderColorClasses = (
  isSelected: boolean,
  isSecondary: boolean,
  matchLevel: "high" | "medium" | "low"
): string => {
  if (isSelected) {
    return "border border-purple-500 bg-gradient-to-br from-purple-50/60 to-purple-100/60 shadow-md ring-purple-200";
  }
  if (isSecondary) {
    return "border border-gray-200 bg-white hover:border-blue-300 hover:shadow-md";
  }

  const matchColors = {
    high: "border border-green-300 bg-white hover:border-green-400 hover:shadow-md",
    medium:
      "border border-yellow-300 bg-white hover:border-yellow-400 hover:shadow-md",
    low: "border border-gray-300 bg-white hover:border-gray-400 hover:shadow-md",
  };

  return matchColors[matchLevel];
};

// Custom hook for sorting and filtering
const useExecutiveSortAndFilter = (
  executives: ExecutiveForUI[],
  excludeId?: string | null,
  /** When set, this executive ID is pinned to the top (e.g. current selection when editing) */
  pinToTopId?: string | null
) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<"name" | "match">("match");
  const [nameSortOrder, setNameSortOrder] = useState<"asc" | "desc">("desc");
  const [matchSortOrder, setMatchSortOrder] = useState<"asc" | "desc">("desc");

  const baseFiltered = useMemo(() => {
    return excludeId ? executives.filter(e => e.id !== excludeId) : executives;
  }, [executives, excludeId]);

  const filteredExecutives = useMemo(() => {
    if (!searchQuery.trim()) return baseFiltered;
    const query = searchQuery.toLowerCase().trim();
    return baseFiltered.filter(exec => {
      const name = (exec.name || "").toLowerCase();
      return name.includes(query);
    });
  }, [baseFiltered, searchQuery]);

  const sortedExecutives = useMemo(() => {
    const sorted = [...filteredExecutives].sort((a, b) => {
      if (sortField === "match") {
        const order = matchSortOrder === "desc" ? -1 : 1;
        return (b.match - a.match) * order;
      } else {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        const order = nameSortOrder === "desc" ? -1 : 1;
        return nameB.localeCompare(nameA) * order;
      }
    });
    // Pin selected ID to top when provided (e.g. initial selection in edit modal)
    if (pinToTopId) {
      const idx = sorted.findIndex(e => String(e.id) === String(pinToTopId));
      if (idx > 0) {
        const [item] = sorted.splice(idx, 1);
        sorted.unshift(item);
      }
    }
    return sorted;
  }, [
    filteredExecutives,
    sortField,
    nameSortOrder,
    matchSortOrder,
    pinToTopId,
  ]);

  const handleMatchSort = useCallback(() => {
    setSortField("match");
    setMatchSortOrder(prev => (prev === "desc" ? "asc" : "desc"));
  }, []);

  const handleNameSort = useCallback(() => {
    setSortField("name");
    setNameSortOrder(prev => (prev === "desc" ? "asc" : "desc"));
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    sortField,
    nameSortOrder,
    matchSortOrder,
    sortedExecutives,
    handleMatchSort,
    handleNameSort,
  };
};

// 1. Executive Card Component
export type ExecutiveCardProps = {
  executive: ExecutiveForUI;
  isSelected: boolean;
  onSelect: () => void;
  showCrossRegionBadge?: boolean;
  variant?: "primary" | "secondary";
  /** Show "AI Match" badge when true (auto mode - AI recommended sponsor) */
  isAiRecommended?: boolean;
  /** Pending count across all selections (e.g. bulk: how many accounts selected this exec). When not provided, uses isSelected ? 1 : 0 */
  pendingCount?: number;
};

export const ExecutiveCard: React.FC<ExecutiveCardProps> = ({
  executive,
  isSelected,
  onSelect,
  showCrossRegionBadge = true,
  variant = "primary",
  isAiRecommended = false,
  pendingCount,
}) => {
  const isSecondary = variant === "secondary";
  const matchLevel = getMatchLevel(executive.match);
  const borderColorClasses = getBorderColorClasses(
    isSelected,
    isSecondary,
    matchLevel
  );
  const baseClasses = `p-4 mb-3 rounded-xl transition-all duration-200 cursor-pointer shadow-sm ${borderColorClasses}`;

  const total = executive.accountLimit ?? 0;
  const used = executive.currentAccounts ?? 0;

  return (
    <div
      className={baseClasses}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      data-match-level={matchLevel}
    >
      <div
        className={`flex flex-wrap justify-between items-start gap-3 ${isSecondary ? "mb-2" : "mb-3"}`}
      >
        <div className="flex gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <h6 className="m-0 font-semibold text-gray-900 text-sm leading-tight">
                {executive.name}
              </h6>
              <span className="inline-flex items-center gap-x-2.5 px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                <span
                  className={`text-xs font-medium ${
                    matchLevel === "high"
                      ? "text-green-600"
                      : matchLevel === "medium"
                        ? "text-yellow-600"
                        : "text-gray-600"
                  }`}
                >
                  Match: {executive.match}%
                </span>
              </span>
              {!isSecondary && isAiRecommended && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-indigo-700 bg-indigo-100 border border-indigo-200 rounded-full whitespace-nowrap">
                  <Zap size={12} className="shrink-0" strokeWidth={2.5} />
                  AI Match
                </span>
              )}
            </div>
            <div className="text-gray-500 text-sm leading-snug">
              {executive.title
                ? executive.title.charAt(0).toUpperCase() +
                  executive.title.slice(1)
                : executive.title}
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 ml-0 md:ml-4 text-right min-w-0">
          {!isSecondary && (
            <CapacityUtilizationBar
              active={used}
              pending={
                pendingCount !== undefined ? pendingCount : isSelected ? 1 : 0
              }
              total={total}
              className="mb-1"
              compact
            />
          )}
        </div>
      </div>

      {!isSecondary &&
        (executive.warnings?.length > 0 ||
          executive.recommendations?.length > 0) && (
          <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {executive.warnings?.length > 0 && (
              <div
                className={`rounded-lg border border-red-200 p-3 ${isSelected ? "bg-red-50/50" : ""}`}
              >
                <div className="text-red-700 font-medium text-sm mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={16} className="shrink-0" />
                  Warnings
                </div>
                <ul className="pl-5 mb-0 space-y-1 text-gray-700 text-sm list-disc">
                  {executive.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            {executive.recommendations?.length > 0 && (
              <div
                className={`rounded-lg border border-green-200 p-3 ${isSelected ? "bg-green-50/50" : ""}`}
              >
                <div className="text-green-700 font-medium text-sm mb-2 flex items-center gap-1.5">
                  <CheckCircle2 size={16} className="shrink-0" />
                  <span>Recommendations</span>
                </div>
                <ul className="pl-5 mb-0 space-y-1 text-gray-700 text-sm font-normal list-disc">
                  {executive.recommendations.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

// 2. Account Details Component
export type AccountDetailsProps = {
  account: UnassignedAccount | null;
  showTitle?: boolean;
};

export const AccountDetails: React.FC<AccountDetailsProps> = ({
  account,
  showTitle = true,
}) => {
  const { formatTier } = useAccountTiers();
  if (!account) return null;

  return (
    <div className="min-w-0">
      {showTitle && (
        <h6 className="font-semibold text-gray-900 mb-3 text-md tracking-tight border-b pb-2">
          {account.name}
        </h6>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
        {account.industry && (
          <div className="flex flex-col min-w-0">
            <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">
              Industry
            </span>
            <span className="text-gray-900 font-medium text-sm truncate">
              {account.industry}
            </span>
          </div>
        )}
        {account.location && (
          <div className="flex flex-col min-w-0">
            <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">
              Location
            </span>
            <span className="text-gray-900 font-medium text-sm truncate">
              {account.location}
            </span>
          </div>
        )}
        <div className="flex flex-col min-w-0">
          <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">
            Tier
          </span>
          <span className="text-gray-900 font-medium text-sm">
            {formatTier(account.tier) || account.tier || "-"}
          </span>
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">
            Contract Value
          </span>
          <span className="text-gray-900 font-medium text-sm">
            {account.value || "-"}
          </span>
        </div>
      </div>
    </div>
  );
};

// 2b. Account Summary Row (for Review Assignment accordion body)
export type AccountSummaryRowProps = {
  account: UnassignedAccount;
  secondaryLabel: string;
  secondaryIsSelected: boolean;
  notes?: string;
  isLast?: boolean;
};

export const AccountSummaryRow: React.FC<AccountSummaryRowProps> = ({
  account,
  secondaryLabel,
  secondaryIsSelected,
  notes,
  isLast = false,
}) => {
  const { formatTier } = useAccountTiers();
  return (
    <div className={`py-3 px-4 ${!isLast ? "border-b border-gray-100" : ""}`}>
      <div className="font-medium text-gray-900 text-sm">{account.name}</div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-600">
        <span className="flex items-center gap-1.5">
          <Building2 size={12} className="text-gray-400 shrink-0" />
          {account.industry}
        </span>
        <span className="flex items-center gap-1.5">
          <MapPin size={12} className="text-gray-400 shrink-0" />
          {account.location}
        </span>
        <span className="text-gray-500 font-medium">
          {formatTier(account.tier) || account.tier}
        </span>
      </div>
      <div className="mt-1.5 text-[11px] font-medium">
        <span className="text-gray-500">Secondary: </span>
        <span
          className={secondaryIsSelected ? "text-gray-900" : "text-amber-600"}
        >
          {secondaryLabel}
        </span>
      </div>
      {notes && (
        <div className="pt-1.5 border-t border-gray-50 text-[11px] text-gray-500">
          Notes: {notes}
        </div>
      )}
    </div>
  );
};

/** Compute utilization percentages from account counts */
export const computeUtilization = (
  currentAccounts: number,
  accountLimit: number,
  accountsToAdd: number
) => {
  const limit = accountLimit || 1;
  const newAccounts = currentAccounts + accountsToAdd;
  const now = limit > 0 ? Math.round((currentAccounts / limit) * 100) : 0;
  const after =
    limit > 0 ? Math.min(100, Math.round((newAccounts / limit) * 100)) : 0;
  return { utilizationNow: now, utilizationAfter: after, newAccounts };
};

// 3. Secondary Executive Selector Component
export type SecondaryExecutiveSelectorProps = {
  executives: ExecutiveForUI[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  excludeId?: string | null;
  /** When set, this ID is pinned to the top of the list (e.g. current secondary when editing) */
  initialSelectedId?: string | null;
};

export const SecondaryExecutiveSelector: React.FC<
  SecondaryExecutiveSelectorProps
> = ({ executives, selectedId, onSelect, excludeId, initialSelectedId }) => {
  const {
    searchQuery,
    setSearchQuery,
    sortField,
    nameSortOrder,
    matchSortOrder,
    sortedExecutives,
    handleMatchSort,
    handleNameSort,
  } = useExecutiveSortAndFilter(executives, excludeId, initialSelectedId);

  // Primary exclude only hides the card; clear stale secondary when it becomes primary
  useEffect(() => {
    if (excludeId && selectedId && excludeId === selectedId) {
      onSelect(null);
    }
  }, [excludeId, selectedId, onSelect]);

  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Section header - always visible, high contrast */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 border-b border-gray-200 flex-shrink-0">
        <h6 className="text-base font-bold text-gray-900 m-0 tracking-wide shrink-0">
          Select Secondary Executive
          <span className="text-gray-600 font-semibold normal-case ml-1">
            (Optional)
          </span>
        </h6>
        <div className="relative group shrink-0">
          <Info
            size={16}
            className="text-gray-600 cursor-help"
            aria-label="Secondary executive info"
          />
          <div className="absolute left-0 top-full mt-1.5 w-72 p-3 text-xs font-normal text-white bg-gray-900 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50 pointer-events-none whitespace-normal">
            Optional secondary executive to provide additional support and
            backup coverage for the account.
          </div>
        </div>
      </div>

      <div className="p-4 pt-3">
        {/* Search and Sort Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex-1 min-w-[200px]">
            <SearchInput
              placeholder="Search executives by name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              rounded="normal"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-700 font-medium shrink-0">
              Sort By:
            </span>
            <Button
              variant={sortField === "match" ? "primary" : "outline-primary"}
              onClick={handleMatchSort}
              className="px-3 py-1.5 text-sm flex items-center gap-2"
            >
              Match %
              {sortField === "match" &&
                (matchSortOrder === "desc" ? (
                  <ArrowDown size={16} />
                ) : (
                  <ArrowUp size={16} />
                ))}
            </Button>
            <Button
              variant={sortField === "name" ? "primary" : "outline-primary"}
              onClick={handleNameSort}
              className="px-3 py-1.5 text-sm flex items-center gap-2"
            >
              {nameSortOrder === "desc" ? (
                <>
                  <ArrowDown size={16} />
                  DESC
                </>
              ) : (
                <>
                  <ArrowUp size={16} />
                  ASC
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="max-h-[min(22rem,40vh)] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-sm p-3">
          <div
            className={`p-4 rounded-lg cursor-pointer transition-colors mb-3 ${
              selectedId === null
                ? "border-2 border-blue-300 bg-blue-50/80 ring-1 ring-blue-200"
                : "border border-gray-200 hover:bg-gray-50 hover:border-gray-300"
            }`}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(null)}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-700 text-sm font-normal">
                No secondary executive
              </span>
              {selectedId === null && (
                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {sortedExecutives.length === 0 ? (
            <div className="text-center py-6 px-4 text-gray-600 text-sm">
              No executives found matching your search.
            </div>
          ) : (
            sortedExecutives.map((exec, index) => (
              <ExecutiveCard
                key={`sec-${exec.id}-${index}`}
                executive={exec}
                isSelected={selectedId === exec.id}
                onSelect={() =>
                  onSelect(selectedId === exec.id ? null : exec.id)
                }
                variant="secondary"
                showCrossRegionBadge={false}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// 4. Assignment Notes Component
export type AssignmentNotesProps = {
  value: string;
  onChange: (value: string) => void;
  name?: string;
};

export const AssignmentNotes: React.FC<AssignmentNotesProps> = ({
  value,
  onChange,
  name = "assignmentNotes",
}) => {
  return (
    <div className="mt-6">
      <TextArea
        name={name}
        label="Assignment Notes (Optional)"
        value={value}
        placeholder="Optional notes about this assignment..."
        onChange={e => onChange(e.target.value)}
        rows={4}
        className="rounded-xl"
      />
    </div>
  );
};

/** Display format: "Full Name — email@domain.com" */
export const formatApproverOptionLabel = (user: {
  full_name?: string | null;
  name?: string | null;
  email?: string | null;
}): string => {
  const name =
    String(user.full_name || "").trim() ||
    String(user.name || "").trim() ||
    String(user.email || "")
      .split("@")[0]
      .trim() ||
    "Unknown";
  const email = String(user.email || "").trim();
  return email ? `${name} — ${email}` : name;
};

export type ApproverSelectOption = { label: string; value: string };

export type ApproverSelectGroup = {
  label: string;
  options: ApproverSelectOption[];
};

const KNOWN_ROLE_VALUES = new Set<string>(
  APPROVER_SELECT_ROLE_GROUPS.flatMap(group => group.roles)
);

const getApproverGroupLabel = (
  roleName: string | null | undefined
): string | null => {
  const normalized = normalizeRoleName(roleName);
  if (!normalized || !KNOWN_ROLE_VALUES.has(normalized)) {
    // Role not in RoleConstants approver groups — leave ungrouped
    return null;
  }

  const matchedGroup = APPROVER_SELECT_ROLE_GROUPS.find(group =>
    group.roles.some(role => role === (normalized as RoleType))
  );
  return matchedGroup?.label ?? null;
};

const resolveApproverRoleName = (
  role: AdminUser["role"] | string | null | undefined
): string | null => {
  if (!role) return null;
  // GET /users/admin returns role as a plain string (role.name)
  if (typeof role === "string") return role;
  if (typeof role === "object" && "name" in role && role.name) {
    return String(role.name);
  }
  return null;
};

/**
 * Build approver dropdown options with "Name — email" labels,
 * grouped from APPROVER_SELECT_ROLE_GROUPS (RoleConstants).
 * Unknown roles are left ungrouped (not forced into a bucket).
 */
export const buildApproverSelectOptions = (
  adminUsers: AdminUser[]
): {
  ungroupedOptions: ApproverSelectOption[];
  groups: ApproverSelectGroup[];
  /** Flat Name — email list (all users) for simple selects */
  flatOptions: ApproverSelectOption[];
} => {
  const ungroupedOptions: ApproverSelectOption[] = [];
  const bucketMap = new Map<string, ApproverSelectOption[]>(
    APPROVER_SELECT_ROLE_GROUPS.map(group => [group.label, []])
  );

  adminUsers.forEach(user => {
    const option: ApproverSelectOption = {
      label: formatApproverOptionLabel(user),
      value: user.user_id,
    };
    const groupLabel = getApproverGroupLabel(
      resolveApproverRoleName(user.role)
    );
    if (!groupLabel) {
      ungroupedOptions.push(option);
      return;
    }
    bucketMap.get(groupLabel)?.push(option);
  });

  ungroupedOptions.sort((a, b) => a.label.localeCompare(b.label));
  bucketMap.forEach(options => {
    options.sort((a, b) => a.label.localeCompare(b.label));
  });

  const groups = APPROVER_SELECT_ROLE_GROUPS.map(group => ({
    label: group.label,
    options: bucketMap.get(group.label) ?? [],
  })).filter(group => group.options.length > 0);

  const flatOptions = [
    ...ungroupedOptions,
    ...groups.flatMap(group => group.options),
  ];

  return {
    ungroupedOptions,
    groups,
    flatOptions,
  };
};

// 5. Batch Name and Approver Form Component
export type BatchApproverFormProps = {
  batchName: string;
  onBatchNameChange: (value: string) => void;
  selectedApprover: string | null;
  onApproverChange: (value: string | null) => void;
  approverOptions: Array<{ label: string; value: string }>;
  approverGroups?: ApproverSelectGroup[];
  adminUsersLoading: boolean;
  adminUsersError: string | null;
  batchNameLabel?: string;
  batchNameName?: string;
  approverName?: string;
};

export const BatchApproverForm: React.FC<BatchApproverFormProps> = ({
  batchName,
  onBatchNameChange,
  selectedApprover,
  onApproverChange,
  approverOptions,
  approverGroups,
  adminUsersLoading,
  adminUsersError,
  batchNameLabel = "Batch Name",
  batchNameName = "batchName",
  approverName = "approver",
}) => {
  return (
    <div className="mt-1">
      <div className="mb-3">
        <CommonInput
          label={batchNameLabel}
          name={batchNameName}
          value={batchName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onBatchNameChange(e?.target?.value ?? "")
          }
          placeholder="Enter batch name for this assignment"
          required
        />
      </div>

      <div className="pt-3 flex-1">
        {adminUsersLoading ? (
          <div className="text-gray-600 text-sm">Loading approvers...</div>
        ) : adminUsersError ? (
          <div className="text-red-600 text-sm font-normal rounded-xl bg-red-50 border border-red-200 px-4 py-3">
            Error loading approvers. Please try again.
          </div>
        ) : (
          <Select
            name={approverName}
            label="Select Approver"
            value={selectedApprover ?? ""}
            onChange={e => onApproverChange(e.target.value || null)}
            options={approverOptions}
            groups={approverGroups}
            placeholder="Choose an approver..."
            required={true}
          />
        )}
      </div>
    </div>
  );
};

// 6. Loading/Error State Component
export type LoadingErrorStateProps = {
  loading: boolean;
  error: string | null;
  loadingText?: string;
  errorText?: string;
};

export const LoadingErrorState: React.FC<LoadingErrorStateProps> = ({
  loading,
  error,
  loadingText = "Loading recommendations...",
  errorText,
}) => {
  if (loading) {
    return (
      <div className="text-center py-8 px-4">
        <p className="text-gray-600 text-sm font-normal m-0">{loadingText}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-4">
        <p className="font-semibold text-red-900 mb-1 text-xs uppercase tracking-wide">
          Error
        </p>
        <p className="text-sm font-normal m-0">{errorText || error}</p>
      </div>
    );
  }

  return null;
};

// 7. Executive List Component
export type ExecutiveListProps = {
  executives: ExecutiveForUI[];
  /** Single selection: one primary executive */
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  /** Multiple selection: compare several executives */
  selectionMode?: "single" | "multiple";
  selectedIds?: string[];
  onToggleSelection?: (id: string) => void;
  mode: "auto" | "manual";
  loading: boolean;
  error: string | null;
  emptyText?: string;
  /** AI recommended sponsor ID - show "AI Match" badge on matching card (auto mode only) */
  aiRecommendedId?: string | null;
  /** When set, this ID is pinned to the top (e.g. current primary when editing). Takes precedence over AI reorder in edit flow. */
  initialSelectedId?: string | null;
  /** For bulk: returns how many accounts have selected this exec as primary. When provided, CapacityUtilizationBar pending uses this. */
  getPendingCountForExecutive?: (execId: string) => number;
};

export const ExecutiveList: React.FC<ExecutiveListProps> = ({
  executives,
  selectedId = null,
  onSelect,
  selectionMode = "single",
  selectedIds = [],
  onToggleSelection,
  mode,
  loading,
  error,
  emptyText = "No executives available",
  aiRecommendedId = null,
  initialSelectedId = null,
  getPendingCountForExecutive,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    sortField,
    nameSortOrder,
    matchSortOrder,
    sortedExecutives: baseSortedExecutives,
    handleMatchSort,
    handleNameSort,
  } = useExecutiveSortAndFilter(executives, undefined, initialSelectedId);

  const sortedExecutives = useMemo(() => {
    // When editing, keep initial selection at top; don't override with AI recommended
    if (initialSelectedId) return baseSortedExecutives;
    if (mode !== "auto" || !aiRecommendedId) return baseSortedExecutives;
    const aiIndex = baseSortedExecutives.findIndex(
      e => String(e.id) === String(aiRecommendedId)
    );
    if (aiIndex <= 0) return baseSortedExecutives;
    const list = [...baseSortedExecutives];
    const [aiExec] = list.splice(aiIndex, 1);
    list.unshift(aiExec);
    return list;
  }, [baseSortedExecutives, mode, aiRecommendedId, initialSelectedId]);

  const isMulti = selectionMode === "multiple";
  const isCardSelected = (id: string) =>
    isMulti ? selectedIds.includes(id) : selectedId === id;
  const handleCardSelect = (exec: ExecutiveForUI) => {
    if (isMulti && onToggleSelection) {
      onToggleSelection(exec.id);
    } else if (!isMulti && onSelect) {
      onSelect(isCardSelected(exec.id) ? null : exec.id);
    }
  };

  if (loading || error) {
    return <LoadingErrorState loading={loading} error={error} />;
  }

  if (executives.length === 0) {
    return <div className="text-center p-4 text-gray-600">{emptyText}</div>;
  }

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Section header - always visible, high contrast */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 border-b border-gray-200 flex-shrink-0">
        <h6 className="text-base font-bold text-gray-900 m-0 tracking-wide shrink-0">
          {isMulti ? "Select Executives" : "Select Primary Executive*"}
        </h6>
        <div className="relative group shrink-0">
          <Info
            size={16}
            className="text-gray-600 cursor-help"
            aria-label="Primary executive info"
          />
          <div className="absolute left-0 top-full mt-1.5 w-72 p-3 text-xs font-normal text-white bg-gray-900 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50 pointer-events-none whitespace-normal">
            {isMulti
              ? "Click cards to select or deselect executives. You can choose multiple to compare before deciding."
              : "Primary executive responsible for the account relationship. Match percentage considers country preference, experience level, industry expertise, and current workload."}
          </div>
        </div>
      </div>

      <div className="p-4 pt-3">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex-1 min-w-[200px]">
            <SearchInput
              placeholder="Search executives by name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              rounded="normal"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-700 font-medium shrink-0">
              Sort By:
            </span>
            <Button
              variant={sortField === "match" ? "primary" : "outline-primary"}
              onClick={handleMatchSort}
              className="px-3 py-1.5 text-sm flex items-center gap-2"
            >
              Match %
              {sortField === "match" &&
                (matchSortOrder === "desc" ? (
                  <ArrowDown size={16} />
                ) : (
                  <ArrowUp size={16} />
                ))}
            </Button>
            <Button
              variant={sortField === "name" ? "primary" : "outline-primary"}
              onClick={handleNameSort}
              className="px-3 py-1.5 text-sm flex items-center gap-2"
            >
              {nameSortOrder === "desc" ? (
                <>
                  <ArrowDown size={16} />
                  DESC
                </>
              ) : (
                <>
                  <ArrowUp size={16} />
                  ASC
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Executive List - tall enough to show multiple cards, scrolls within section */}
        <div className="max-h-[min(22rem,40vh)] overflow-y-auto bg-gray-50/50 border border-gray-200 rounded-xl p-3">
          {sortedExecutives.length === 0 ? (
            <div className="text-center py-6 px-4 text-gray-600 text-sm">
              No executives found matching your search.
            </div>
          ) : (
            sortedExecutives.map((exec, index) => (
              <ExecutiveCard
                key={`exec-${exec.id}-${index}`}
                executive={exec}
                isSelected={isCardSelected(exec.id)}
                onSelect={() => handleCardSelect(exec)}
                showCrossRegionBadge={true}
                isAiRecommended={
                  mode === "auto" &&
                  !!aiRecommendedId &&
                  String(exec.id) === String(aiRecommendedId)
                }
                pendingCount={
                  getPendingCountForExecutive
                    ? getPendingCountForExecutive(exec.id)
                    : undefined
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// 8. Modal Actions Component
export type ModalActionsProps = {
  onCancel: () => void;
  onNext?: () => void;
  onBack?: () => void;
  nextLabel?: string;
  backLabel?: string;
  cancelLabel?: string;
  nextDisabled?: boolean;
  showBack?: boolean;
  align?: "left" | "right" | "between";
};

export const ModalActions: React.FC<ModalActionsProps> = ({
  onCancel,
  onNext,
  onBack,
  nextLabel = "Next",
  backLabel = "Back",
  cancelLabel = "Cancel",
  nextDisabled = false,
  showBack = false,
  align = "right",
}) => {
  const alignClass =
    align === "between"
      ? "justify-between"
      : align === "left"
        ? "justify-start"
        : "justify-end";

  return (
    <div
      className={`flex flex-wrap ${alignClass} gap-3 mt-6 pt-4 border-t border-gray-200`}
    >
      {showBack && onBack && (
        <Button variant="outline-primary" onClick={onBack}>
          {backLabel}
        </Button>
      )}
      <Button variant="outline-primary" onClick={onCancel}>
        {cancelLabel}
      </Button>
      {onNext && (
        <Button variant="primary" onClick={onNext} disabled={nextDisabled}>
          {nextLabel}
        </Button>
      )}
    </div>
  );
};

// Helper function to map API executive to UI format
export const mapExecutiveToUI = (exec: ExecutiveFromAPI): ExecutiveForUI => {
  const utilizationPercent = exec.utilization || 0;
  const availability = exec.availabilityStatus || "Unknown";
  const execAny = exec as ExecutiveFromAPI & { full_name?: string };
  const name = exec.name || execAny.full_name || "";

  return {
    id: exec.id,
    name,
    title: `${exec.title} • ${exec.region || "Unknown"}`,
    availability: availability,
    utilization: utilizationPercent,
    region: exec.region || "Unknown",
    match: exec.matchScore || 0,
    warnings: exec.warnings || [],
    recommendations: exec.recommendations || [],
    accountLimit: exec.accountLimit ?? 0,
    currentAccounts: exec.current_accounts ?? 0,
  };
};
